import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';

// ====================================================================
// Teams.js — Card grid layout for team management
// Matches the dark theme used by Agents.js
// ====================================================================

const TEAM_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b',
  '#10b981', '#06b6d4', '#3b82f6', '#14b8a6', '#f97316'
];

const Teams = () => {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';

  // ===== State =====
  const [teams, setTeams] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTeam, setDetailTeam] = useState(null);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);

  const initialForm = {
    name: '',
    name_ar: '',
    description: '',
    leader_id: '',
    color: TEAM_COLORS[0],
    member_ids: []
  };
  const [formData, setFormData] = useState(initialForm);

  // ===== Translations =====
  const t = {
    title: isAr ? 'الفرق' : 'Teams',
    subtitle: isAr ? 'إدارة فرق الموظفين والمسؤولين' : 'Manage agent teams and their leaders',
    addNew: isAr ? 'فريق جديد +' : 'Add New Team +',
    searchPlaceholder: isAr ? 'بحث عن فريق...' : 'Search teams...',
    noTeams: isAr ? 'لا توجد فرق بعد. أنشئ أول فريق لك.' : 'No teams yet. Create your first team.',
    leader: isAr ? 'المسؤول' : 'Leader',
    members: isAr ? 'الأعضاء' : 'Members',
    member: isAr ? 'عضو' : 'member',
    members_plural: isAr ? 'أعضاء' : 'members',
    edit: isAr ? 'تعديل' : 'Edit',
    delete: isAr ? 'حذف' : 'Delete',
    view: isAr ? 'عرض' : 'View',
    addMember: isAr ? 'إضافة عضو +' : 'Add Member +',
    removeMember: isAr ? 'إزالة' : 'Remove',
    save: isAr ? 'حفظ' : 'Save',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    create: isAr ? 'إنشاء' : 'Create',
    update: isAr ? 'تحديث' : 'Update',
    createTeam: isAr ? 'إنشاء فريق' : 'Create Team',
    editTeam: isAr ? 'تعديل الفريق' : 'Edit Team',
    teamDetails: isAr ? 'تفاصيل الفريق' : 'Team Details',
    addMembersTitle: isAr ? 'إضافة أعضاء' : 'Add Members',
    nameEn: isAr ? 'الاسم (إنجليزي)' : 'Name (English)',
    nameAr: isAr ? 'الاسم (عربي)' : 'Name (Arabic)',
    description: isAr ? 'الوصف' : 'Description',
    leaderLabel: isAr ? 'مسؤول الفريق' : 'Team Leader',
    color: isAr ? 'اللون' : 'Color',
    initialMembers: isAr ? 'الأعضاء الأوليون (اختياري)' : 'Initial Members (optional)',
    selectLeader: isAr ? '-- اختر المسؤول --' : '-- Select Leader --',
    confirmDelete: isAr ? 'هل أنت متأكد من حذف هذا الفريق؟' : 'Are you sure you want to delete this team?',
    confirmRemove: isAr ? 'إزالة هذا العضو من الفريق؟' : 'Remove this member from the team?',
    required: isAr ? 'مطلوب' : 'Required',
    nameRequired: isAr ? 'الاسم مطلوب' : 'Name is required',
    leaderRequired: isAr ? 'المسؤول مطلوب' : 'Leader is required',
    saveError: isAr ? 'فشل الحفظ' : 'Save failed',
    deleteError: isAr ? 'فشل الحذف' : 'Delete failed',
    loadError: isAr ? 'فشل تحميل البيانات' : 'Failed to load data',
    noResults: isAr ? 'لا توجد نتائج' : 'No results',
    add: isAr ? 'إضافة' : 'Add',
    selected: isAr ? 'محدد' : 'selected',
    role_admin: isAr ? 'مدير' : 'Admin',
    role_supervisor: isAr ? 'مشرف' : 'Supervisor',
    role_agent: isAr ? 'موظف' : 'Agent'
  };

  const roleLabel = (r) => t[`role_${r}`] || r;

  // ===== API helpers =====
  const apiCall = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Request failed');
    }
    return data;
  }, []);

  // ===== Data loaders =====
  const loadTeams = useCallback(async () => {
    try {
      const data = await apiCall('/api/v1/teams');
      setTeams(data.data || []);
    } catch (err) {
      console.error(err);
      setError(t.loadError);
    }
  }, [apiCall, t.loadError]);

  const loadAvailableAgents = useCallback(async () => {
    try {
      const data = await apiCall('/api/v1/teams/agents/available');
      setAvailableAgents(data.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [apiCall]);

  useEffect(() => {
    Promise.all([loadTeams(), loadAvailableAgents()]).finally(() => setLoading(false));
  }, [loadTeams, loadAvailableAgents]);

  // ===== Form handlers =====
  const openCreateForm = () => {
    setEditingTeam(null);
    setFormData(initialForm);
    setShowFormModal(true);
  };

  const openEditForm = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      name_ar: team.name_ar || '',
      description: team.description || '',
      leader_id: team.leader_id,
      color: team.color || TEAM_COLORS[0],
      member_ids: []
    });
    setShowFormModal(true);
  };

  const handleSaveForm = async () => {
    if (!formData.name.trim()) { alert(t.nameRequired); return; }
    if (!formData.leader_id) { alert(t.leaderRequired); return; }

    try {
      if (editingTeam) {
        await apiCall(`/api/v1/teams/${editingTeam.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: formData.name,
            name_ar: formData.name_ar || null,
            description: formData.description || null,
            leader_id: parseInt(formData.leader_id, 10),
            color: formData.color
          })
        });
      } else {
        await apiCall('/api/v1/teams', {
          method: 'POST',
          body: JSON.stringify({
            name: formData.name,
            name_ar: formData.name_ar || null,
            description: formData.description || null,
            leader_id: parseInt(formData.leader_id, 10),
            color: formData.color,
            member_ids: formData.member_ids.map(id => parseInt(id, 10))
          })
        });
      }
      setShowFormModal(false);
      await loadTeams();
    } catch (err) {
      alert(`${t.saveError}: ${err.message}`);
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(`${t.confirmDelete}\n"${team.name}"`)) return;
    try {
      await apiCall(`/api/v1/teams/${team.id}`, { method: 'DELETE' });
      await loadTeams();
    } catch (err) {
      alert(`${t.deleteError}: ${err.message}`);
    }
  };

  // ===== Detail modal =====
  const openDetail = async (team) => {
    try {
      const data = await apiCall(`/api/v1/teams/${team.id}`);
      setDetailTeam(data.data);
      setShowDetailModal(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const refreshDetail = async () => {
    if (!detailTeam) return;
    const data = await apiCall(`/api/v1/teams/${detailTeam.id}`);
    setDetailTeam(data.data);
  };

  const handleRemoveMember = async (agentId) => {
    if (!window.confirm(t.confirmRemove)) return;
    try {
      await apiCall(`/api/v1/teams/${detailTeam.id}/members/${agentId}`, { method: 'DELETE' });
      await refreshDetail();
      await loadTeams();
    } catch (err) {
      alert(err.message);
    }
  };

  // ===== Add members modal =====
  const openAddMembersModal = () => {
    setSelectedNewMembers([]);
    setMemberSearchQuery('');
    setShowAddMembersModal(true);
  };

  const handleAddMembers = async () => {
    if (selectedNewMembers.length === 0) {
      setShowAddMembersModal(false);
      return;
    }
    try {
      await apiCall(`/api/v1/teams/${detailTeam.id}/members`, {
        method: 'POST',
        body: JSON.stringify({ agent_ids: selectedNewMembers })
      });
      setShowAddMembersModal(false);
      await refreshDetail();
      await loadTeams();
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleNewMember = (agentId) => {
    setSelectedNewMembers(prev =>
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };

  const toggleInitialMember = (agentId) => {
    setFormData(fm => ({
      ...fm,
      member_ids: fm.member_ids.includes(agentId)
        ? fm.member_ids.filter(id => id !== agentId)
        : [...fm.member_ids, agentId]
    }));
  };

  // ===== Filtering for add-members modal =====
  const existingMemberIds = detailTeam ? detailTeam.members.map(m => m.id) : [];
  const filteredAgentsForAdd = availableAgents
    .filter(a => !existingMemberIds.includes(a.id))
    .filter(a => a.full_name.toLowerCase().includes(memberSearchQuery.toLowerCase()));

  // ===== Styles =====
  const styles = {
    container: { padding: '24px', minHeight: 'calc(100vh - 64px)' },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      marginBottom: '32px', flexWrap: 'wrap', gap: '16px'
    },
    title: { fontSize: '28px', fontWeight: '700', color: '#f9fafb', margin: 0 },
    subtitle: { fontSize: '14px', color: '#9ca3af', marginTop: '6px' },
    addBtn: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px',
      fontSize: '14px', fontWeight: '600', cursor: 'pointer',
      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px'
    },
    card: {
      background: 'rgba(26, 32, 56, 0.6)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '14px', padding: '20px', cursor: 'pointer',
      transition: 'all 0.2s', position: 'relative', overflow: 'hidden'
    },
    cardColorBar: { position: 'absolute', top: 0, left: 0, right: 0, height: '4px' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
    cardName: { fontSize: '17px', fontWeight: '700', color: '#f9fafb', marginBottom: '4px' },
    cardNameAr: { fontSize: '13px', color: '#9ca3af' },
    cardLeader: {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px', background: 'rgba(255,255,255,0.03)',
      borderRadius: '8px', marginBottom: '14px'
    },
    leaderAvatar: {
      width: '36px', height: '36px', borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '14px', fontWeight: '700', color: '#fff',
      flexShrink: 0
    },
    leaderInfo: { flex: 1, minWidth: 0 },
    leaderLabel: { fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
    leaderName: { fontSize: '13px', color: '#e5e7eb', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    cardStats: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.06)',
      marginBottom: '14px'
    },
    memberCount: { fontSize: '13px', color: '#9ca3af' },
    memberCountNumber: { fontSize: '20px', fontWeight: '700', color: '#f9fafb' },
    cardActions: { display: 'flex', gap: '8px' },
    actionBtn: {
      flex: 1, padding: '8px', border: 'none', borderRadius: '6px',
      fontSize: '12px', fontWeight: '600', cursor: 'pointer',
      transition: 'opacity 0.2s'
    },
    viewBtn: { background: '#10b981', color: '#fff' },
    editBtn: { background: '#f59e0b', color: '#fff' },
    deleteBtn: { background: '#ef4444', color: '#fff' },
    empty: {
      textAlign: 'center', padding: '60px 20px', color: '#9ca3af', fontSize: '15px'
    },
    // Modal
    modalOverlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    },
    modal: {
      background: '#1a2038', borderRadius: '14px',
      width: '100%', maxWidth: '600px', maxHeight: '90vh',
      overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)'
    },
    modalLarge: { maxWidth: '760px' },
    modalHeader: {
      padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    modalTitle: { fontSize: '18px', fontWeight: '700', color: '#f9fafb', margin: 0 },
    closeBtn: {
      background: 'transparent', border: 'none', color: '#9ca3af',
      fontSize: '24px', cursor: 'pointer', padding: '0', lineHeight: 1
    },
    modalBody: { padding: '24px' },
    modalFooter: {
      padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', justifyContent: 'flex-end', gap: '10px'
    },
    field: { marginBottom: '16px' },
    label: {
      display: 'block', fontSize: '13px', color: '#d1d5db',
      marginBottom: '6px', fontWeight: '500'
    },
    input: {
      width: '100%', padding: '10px 12px',
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px', color: '#f9fafb', fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%', padding: '10px 12px', minHeight: '70px',
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px', color: '#f9fafb', fontSize: '14px',
      boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit'
    },
    colorSwatches: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    swatch: {
      width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer',
      border: '2px solid transparent', transition: 'all 0.15s'
    },
    swatchActive: { border: '2px solid #fff', transform: 'scale(1.1)' },
    primaryBtn: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      color: '#fff', border: 'none', padding: '10px 22px',
      borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
    },
    secondaryBtn: {
      background: 'transparent', color: '#9ca3af',
      border: '1px solid rgba(255,255,255,0.15)', padding: '10px 22px',
      borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
    },
    memberList: {
      display: 'flex', flexDirection: 'column', gap: '8px',
      maxHeight: '320px', overflowY: 'auto', padding: '4px'
    },
    memberRow: {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px', background: 'rgba(255,255,255,0.03)',
      borderRadius: '8px', cursor: 'pointer'
    },
    memberRowSelected: {
      background: 'rgba(59,130,246,0.15)',
      border: '1px solid rgba(59,130,246,0.4)'
    },
    avatar: {
      width: '34px', height: '34px', borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0
    },
    memberInfo: { flex: 1, minWidth: 0 },
    memberName: { fontSize: '14px', color: '#f9fafb', fontWeight: '500' },
    roleBadge: {
      display: 'inline-block', padding: '3px 8px', borderRadius: '6px',
      fontSize: '11px', fontWeight: '600', textTransform: 'uppercase'
    },
    leaderBadgeColors: { background: 'rgba(245,158,11,0.2)', color: '#fbbf24' },
    supervisorBadgeColors: { background: 'rgba(139,92,246,0.2)', color: '#a78bfa' },
    agentBadgeColors: { background: 'rgba(59,130,246,0.2)', color: '#60a5fa' }
  };

  const getRoleBadgeStyle = (role) => {
    if (role === 'admin') return { ...styles.roleBadge, ...styles.leaderBadgeColors };
    if (role === 'supervisor') return { ...styles.roleBadge, ...styles.supervisorBadgeColors };
    return { ...styles.roleBadge, ...styles.agentBadgeColors };
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  };

  const getAvatarColor = (id) => {
    return TEAM_COLORS[id % TEAM_COLORS.length];
  };

  // ===== Render =====
  if (loading) {
    return (
      <Layout>
        <div style={{ ...styles.container, textAlign: 'center', color: '#9ca3af' }}>
          {isAr ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container} dir={isAr ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>{t.title}</h1>
            <div style={styles.subtitle}>{t.subtitle}</div>
          </div>
          <button style={styles.addBtn} onClick={openCreateForm}>{t.addNew}</button>
        </div>

        {error && (
          <div style={{ padding: '12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', borderRadius: '8px', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {/* Card grid */}
        {teams.length === 0 ? (
          <div style={styles.empty}>{t.noTeams}</div>
        ) : (
          <div style={styles.grid}>
            {teams.map(team => (
              <div
                key={team.id}
                style={styles.card}
                onClick={() => openDetail(team)}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                <div style={{ ...styles.cardColorBar, background: team.color || '#6366f1' }} />

                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.cardName}>{team.name}</div>
                    {team.name_ar && <div style={styles.cardNameAr}>{team.name_ar}</div>}
                  </div>
                </div>

                <div style={styles.cardLeader}>
                  <div style={{ ...styles.leaderAvatar, background: getAvatarColor(team.leader_id) }}>
                    {getInitials(team.leader_name)}
                  </div>
                  <div style={styles.leaderInfo}>
                    <div style={styles.leaderLabel}>{t.leader}</div>
                    <div style={styles.leaderName}>{team.leader_name}</div>
                  </div>
                  <span style={getRoleBadgeStyle(team.leader_role)}>{roleLabel(team.leader_role)}</span>
                </div>

                <div style={styles.cardStats}>
                  <span style={styles.memberCount}>{t.members}</span>
                  <span style={styles.memberCountNumber}>{team.member_count}</span>
                </div>

                <div style={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <button style={{ ...styles.actionBtn, ...styles.viewBtn }} onClick={() => openDetail(team)}>{t.view}</button>
                  <button style={{ ...styles.actionBtn, ...styles.editBtn }} onClick={() => openEditForm(team)}>{t.edit}</button>
                  <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleDelete(team)}>{t.delete}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== Create/Edit Modal ===== */}
        {showFormModal && (
          <div style={styles.modalOverlay} onClick={() => setShowFormModal(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{editingTeam ? t.editTeam : t.createTeam}</h3>
                <button style={styles.closeBtn} onClick={() => setShowFormModal(false)}>×</button>
              </div>
              <div style={styles.modalBody}>
                <div style={styles.field}>
                  <label style={styles.label}>{t.nameEn} *</label>
                  <input
                    style={styles.input}
                    value={formData.name}
                    onChange={(e) => setFormData(fm => ({ ...fm, name: e.target.value }))}
                    placeholder="e.g. Sales Team"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{t.nameAr}</label>
                  <input
                    style={styles.input}
                    value={formData.name_ar}
                    onChange={(e) => setFormData(fm => ({ ...fm, name_ar: e.target.value }))}
                    placeholder="مثل: فريق المبيعات"
                    dir="rtl"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{t.description}</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.description}
                    onChange={(e) => setFormData(fm => ({ ...fm, description: e.target.value }))}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{t.leaderLabel} *</label>
                  <select
                    style={styles.input}
                    value={formData.leader_id}
                    onChange={(e) => setFormData(fm => ({ ...fm, leader_id: e.target.value }))}
                  >
                    <option value="">{t.selectLeader}</option>
                    {availableAgents.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.full_name} — {roleLabel(a.role)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{t.color}</label>
                  <div style={styles.colorSwatches}>
                    {TEAM_COLORS.map(c => (
                      <div
                        key={c}
                        style={{
                          ...styles.swatch,
                          background: c,
                          ...(formData.color === c ? styles.swatchActive : {})
                        }}
                        onClick={() => setFormData(fm => ({ ...fm, color: c }))}
                      />
                    ))}
                  </div>
                </div>

                {!editingTeam && (
                  <div style={styles.field}>
                    <label style={styles.label}>
                      {t.initialMembers}
                      {formData.member_ids.length > 0 && (
                        <span style={{ marginLeft: '8px', color: '#60a5fa', fontSize: '12px' }}>
                          ({formData.member_ids.length} {t.selected})
                        </span>
                      )}
                    </label>
                    <div style={styles.memberList}>
                      {availableAgents
                        .filter(a => parseInt(formData.leader_id, 10) !== a.id)
                        .map(a => (
                          <div
                            key={a.id}
                            style={{
                              ...styles.memberRow,
                              ...(formData.member_ids.includes(a.id) ? styles.memberRowSelected : {})
                            }}
                            onClick={() => toggleInitialMember(a.id)}
                          >
                            <input
                              type="checkbox"
                              checked={formData.member_ids.includes(a.id)}
                              readOnly
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <div style={{ ...styles.avatar, background: getAvatarColor(a.id) }}>
                              {getInitials(a.full_name)}
                            </div>
                            <div style={styles.memberInfo}>
                              <div style={styles.memberName}>{a.full_name}</div>
                            </div>
                            <span style={getRoleBadgeStyle(a.role)}>{roleLabel(a.role)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={styles.modalFooter}>
                <button style={styles.secondaryBtn} onClick={() => setShowFormModal(false)}>{t.cancel}</button>
                <button style={styles.primaryBtn} onClick={handleSaveForm}>
                  {editingTeam ? t.update : t.create}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== Team Detail Modal ===== */}
        {showDetailModal && detailTeam && (
          <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
            <div style={{ ...styles.modal, ...styles.modalLarge }} onClick={(e) => e.stopPropagation()}>
              <div style={{ ...styles.modalHeader, background: `linear-gradient(90deg, ${detailTeam.color || '#6366f1'}30, transparent)` }}>
                <div>
                  <h3 style={styles.modalTitle}>{detailTeam.name}</h3>
                  {detailTeam.name_ar && <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>{detailTeam.name_ar}</div>}
                </div>
                <button style={styles.closeBtn} onClick={() => setShowDetailModal(false)}>×</button>
              </div>
              <div style={styles.modalBody}>
                {detailTeam.description && (
                  <div style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '20px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    {detailTeam.description}
                  </div>
                )}

                {/* Leader card */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    {t.leader}
                  </div>
                  <div style={{ ...styles.cardLeader, marginBottom: 0 }}>
                    <div style={{ ...styles.leaderAvatar, background: getAvatarColor(detailTeam.leader_id), width: '44px', height: '44px', fontSize: '16px' }}>
                      {getInitials(detailTeam.leader_name)}
                    </div>
                    <div style={styles.leaderInfo}>
                      <div style={{ ...styles.leaderName, fontSize: '15px' }}>{detailTeam.leader_name}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{detailTeam.leader_email}</div>
                    </div>
                    <span style={getRoleBadgeStyle(detailTeam.leader_role)}>{roleLabel(detailTeam.leader_role)}</span>
                  </div>
                </div>

                {/* Members section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t.members} ({detailTeam.members.length})
                  </div>
                  <button style={{ ...styles.primaryBtn, padding: '6px 14px', fontSize: '12px' }} onClick={openAddMembersModal}>
                    {t.addMember}
                  </button>
                </div>

                {detailTeam.members.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                    {isAr ? 'لا يوجد أعضاء بعد' : 'No members yet'}
                  </div>
                ) : (
                  <div style={styles.memberList}>
                    {detailTeam.members.map(m => (
                      <div key={m.id} style={styles.memberRow}>
                        <div style={{ ...styles.avatar, background: getAvatarColor(m.id) }}>
                          {getInitials(m.full_name)}
                        </div>
                        <div style={styles.memberInfo}>
                          <div style={styles.memberName}>{m.full_name}</div>
                        </div>
                        <span style={getRoleBadgeStyle(m.role)}>{roleLabel(m.role)}</span>
                        <button
                          style={{ ...styles.actionBtn, ...styles.deleteBtn, flex: '0 0 auto', padding: '6px 12px' }}
                          onClick={() => handleRemoveMember(m.id)}
                        >
                          {t.removeMember}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== Add Members Modal ===== */}
        {showAddMembersModal && (
          <div style={styles.modalOverlay} onClick={() => setShowAddMembersModal(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>
                  {t.addMembersTitle}
                  {selectedNewMembers.length > 0 && (
                    <span style={{ color: '#60a5fa', fontSize: '13px', marginLeft: '10px' }}>
                      ({selectedNewMembers.length} {t.selected})
                    </span>
                  )}
                </h3>
                <button style={styles.closeBtn} onClick={() => setShowAddMembersModal(false)}>×</button>
              </div>
              <div style={styles.modalBody}>
                <input
                  style={{ ...styles.input, marginBottom: '14px' }}
                  placeholder={t.searchPlaceholder}
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                />
                {filteredAgentsForAdd.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                    {t.noResults}
                  </div>
                ) : (
                  <div style={styles.memberList}>
                    {filteredAgentsForAdd.map(a => (
                      <div
                        key={a.id}
                        style={{
                          ...styles.memberRow,
                          ...(selectedNewMembers.includes(a.id) ? styles.memberRowSelected : {})
                        }}
                        onClick={() => toggleNewMember(a.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedNewMembers.includes(a.id)}
                          readOnly
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <div style={{ ...styles.avatar, background: getAvatarColor(a.id) }}>
                          {getInitials(a.full_name)}
                        </div>
                        <div style={styles.memberInfo}>
                          <div style={styles.memberName}>{a.full_name}</div>
                        </div>
                        <span style={getRoleBadgeStyle(a.role)}>{roleLabel(a.role)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.modalFooter}>
                <button style={styles.secondaryBtn} onClick={() => setShowAddMembersModal(false)}>{t.cancel}</button>
                <button style={styles.primaryBtn} onClick={handleAddMembers} disabled={selectedNewMembers.length === 0}>
                  {t.add} ({selectedNewMembers.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Teams;
